import type { RangeInfo } from '@muzly/voice-engine';
import { freqToMidi } from '@muzly/voice-engine';

/**
 * voiceTypes — 측정한 음역 → **예상 보이스 타입**(성부) + 비슷한 음역대의 가수.
 *
 * 덱 p10이 요구하는 문장("바리톤 계열 * 김동률, 성시경과 비슷한 음역대예요" — 화면은 한 줄로 줄인 `비슷해요 (추정)`)의 근거다.
 *
 * **엔진의 `fach`를 안 쓰는 이유**: `FachKey`는 soprano/alto/tenor/bass(+성별 미상 조합)
 * 넷뿐이라 덱이 이름을 대는 **바리톤·메조소프라노**를 표현할 수 없다. 화면은 이 파일의
 * 6성부 하나만 낸다 — 두 분류를 같이 그리면 사용자는 서로 다른 두 진단으로 읽는다.
 *
 * **데이터 출처는 추정이다.** 성부별 음역은 성악 관례로 통용되는 대략적인 구간이고(합창
 * 파트 나누기에서 흔히 쓰는 폭), 가수 매핑은 발표곡에서 들리는 음역대에 대한 **대중적
 * 인상**이지 실측이 아니다. 정식 데이터가 생기면 이 표만 갈아 끼운다.
 *
 * React·IO 의존 없음 — vitest 대상.
 */

export type VoiceGender = 'female' | 'male' | 'unknown';

export interface VoiceTypeInfo {
  id: string;
  /** 성부 이름 i18n 키 — 화면 문구는 여기 없다. */
  labelKey: string;
  /** 어느 성별 후보군인지. 성별 답이 없으면 전부를 후보로 본다. */
  gender: Exclude<VoiceGender, 'unknown'>;
  /** 대표 음역(midi). 판정은 이 구간의 **중심**과의 거리로 한다. */
  lowMidi: number;
  highMidi: number;
  /**
   * 비슷한 음역대의 가수 i18n 키(한국 가수 기준 — 덱이 김동률·성시경을 예로 든다).
   * 이름도 로케일을 탄다: en은 각 가수가 활동에 쓰는 영문 표기다.
   */
  singerKeys: string[];
}

const singerKey = (id: string): string => `web.result.range.singers.${id}`;

/**
 * 성부 표. midi 60 = C4(가온다).
 *
 * 구간은 "그 성부가 편하게 쓰는 폭"에 가깝다(극단적인 무대 음역이 아니라) — 사용자의
 * 진단 음역은 무리하지 않은 범위에서 나오기 때문이다.
 */
export const VOICE_TYPES: VoiceTypeInfo[] = [
  {
    id: 'bass',
    labelKey: 'web.result.range.types.bass',
    gender: 'male',
    lowMidi: 40, // E2
    highMidi: 62, // D4
    singerKeys: [singerKey('choiBaekho'), singerKey('kimKwangseok')],
  },
  {
    id: 'baritone',
    labelKey: 'web.result.range.types.baritone',
    gender: 'male',
    lowMidi: 45, // A2
    highMidi: 65, // F4
    singerKeys: [singerKey('kimDongryul'), singerKey('sungSikyung')],
  },
  {
    id: 'tenor',
    labelKey: 'web.result.range.types.tenor',
    gender: 'male',
    lowMidi: 48, // C3
    highMidi: 72, // C5
    singerKeys: [singerKey('parkHyoshin'), singerKey('kimBumsoo')],
  },
  {
    id: 'contralto',
    labelKey: 'web.result.range.types.contralto',
    gender: 'female',
    lowMidi: 53, // F3
    highMidi: 74, // D5
    singerKeys: [singerKey('leeSora'), singerKey('jangJaein')],
  },
  {
    id: 'mezzo',
    labelKey: 'web.result.range.types.mezzo',
    gender: 'female',
    lowMidi: 55, // G3
    highMidi: 77, // F5
    singerKeys: [singerKey('iu'), singerKey('taeyeon')],
  },
  {
    id: 'soprano',
    labelKey: 'web.result.range.types.soprano',
    gender: 'female',
    lowMidi: 60, // C4
    highMidi: 84, // C6
    singerKeys: [singerKey('parkJunghyun'), singerKey('sohyang')],
  },
];

const HANGUL_FIRST = 0xac00;
const HANGUL_LAST = 0xd7a3;
const JONGSEONG_COUNT = 28;

/**
 * 마지막 글자에 받침이 있나 — "성시경**과**" / "아이유**와**"를 가르는 한국어 조사 규칙.
 * 문구를 키 둘로 나눠 두고 화면이 이 함수로 고른다(보간으로는 조사를 못 바꾼다).
 * 한글 음절이 아니면(영문 이름 등) 받침 없는 쪽으로 본다.
 */
export const hasFinalConsonant = (word: string): boolean => {
  const trimmed = word.trim();
  const code = trimmed.charCodeAt(trimmed.length - 1);
  if (Number.isNaN(code) || code < HANGUL_FIRST || code > HANGUL_LAST) {
    return false;
  }
  return (code - HANGUL_FIRST) % JONGSEONG_COUNT !== 0;
};

const toMidi = freqToMidi;

/** 설문의 성별 답 → 후보군. 'none'·무응답은 모르는 것이지 중립 성부가 아니다. */
const toVoiceGender = (gender: string | undefined): VoiceGender =>
  gender === 'female' || gender === 'male' ? gender : 'unknown';

/**
 * 음역의 **중심**이 어느 성부의 중심에 가장 가까운가. 양끝을 각각 맞추지 않는 이유:
 * 진단 음역은 사용자가 실제로 낸 폭이라 성부 표보다 훨씬 좁게 나오는 게 보통이고,
 * 그러면 어느 성부와도 양끝이 안 맞는다. 중심(=편한 음역대)은 그 영향을 덜 받는다.
 */
export const estimateVoiceType = ({
  range,
  gender,
}: {
  range: RangeInfo | null;
  gender: string | undefined;
}): VoiceTypeInfo | undefined => {
  if (!range?.lowHz || !range?.highHz) {
    return undefined;
  }
  const voiceGender = toVoiceGender(gender);
  const candidates =
    voiceGender === 'unknown'
      ? VOICE_TYPES
      : VOICE_TYPES.filter((t) => t.gender === voiceGender);
  const center = (toMidi(range.lowHz) + toMidi(range.highHz)) / 2;
  const distance = (t: VoiceTypeInfo): number =>
    Math.abs(center - (t.lowMidi + t.highMidi) / 2);
  return candidates.reduce((best, t) =>
    distance(t) < distance(best) ? t : best,
  );
};
